import React, { memo, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { useLocation, useParams } from 'react-router-dom';
import dayjs from 'utils/dayjs';

import { useBreadcrumbs, useSeoMeta } from 'hooks';
import { DEFAULT_API_DATE_FORMAT, getLocaleUrlPath } from 'utils';
import { getMbPath } from 'utils/mbArchive';

import BreadCrumbs from 'components/BreadCrumbs';

const MbPageBreadCrumbs = () => {
  const location = useLocation();
  const { pageTitle } = useSeoMeta();
  const { locale, formatMessage } = useIntl();
  const { currency, date, dateFrom, dateTo } = useParams();
  const isArchivePage = location.pathname.includes('/mb/archive/');
  const mbArchive = useSelector(state => state.currency?.mbArchive);

  const currentCurrency = useSelector(state => {
    if (!currency) {
      return null;
    }

    const list = state.currency?.currencyList || [];

    return (
      list.find(item => item?.name?.toLowerCase() === currency.toLowerCase()) ||
      null
    );
  });

  const currencyLabel =
    currentCurrency?.nameG || currentCurrency?.name || currency?.toUpperCase();

  const localeInURL = getLocaleUrlPath(locale);
  const routerBasename = `${localeInURL}${process.env.APP_ROUTER_BASENAME}`;

  const archiveBreadcrumbTitle = useMemo(() => {
    const isRange = Boolean(
      dateTo || (mbArchive?.mode === 'range' && mbArchive?.dateTo)
    );
    const rangeFrom = dateFrom || mbArchive?.date;
    const rangeTo = dateTo || mbArchive?.dateTo;

    if (isRange && rangeFrom && rangeTo) {
      const from = dayjs(rangeFrom, DEFAULT_API_DATE_FORMAT, true).format(
        'DD.MM.YYYY'
      );
      const to = dayjs(rangeTo, DEFAULT_API_DATE_FORMAT, true).format(
        'DD.MM.YYYY'
      );

      return formatMessage(
        { id: 'mbArchivePage.breadcrumb.range' },
        { currency: currencyLabel, dateRange: `${from}-${to}` }
      );
    }

    const archiveDate = date || dateFrom || mbArchive?.date;
    const formattedDate = dayjs(archiveDate, DEFAULT_API_DATE_FORMAT, true)
      .locale(locale)
      .format('D MMMM YYYY');

    return formatMessage(
      { id: 'mbArchivePage.breadcrumb.day' },
      { currency: currencyLabel, date: formattedDate }
    );
  }, [date, dateFrom, dateTo, mbArchive, formatMessage, currencyLabel, locale]);

  const breadcrumbsPlaceholders = useMemo(() => {
    const base = [
      {
        name: formatMessage({ id: 'breadcrumbs.mainPage' }),
        link: getLocaleUrlPath(locale),
      },
      {
        name: formatMessage({ id: 'breadcrumbs.currencyPage' }),
        link: routerBasename,
      },
    ];

    if (isArchivePage) {
      return [
        ...base,
        {
          name: formatMessage({ id: 'interbank' }),
          link: `${routerBasename}${getMbPath(currency)}`,
        },
        {
          name: archiveBreadcrumbTitle,
          link: `${routerBasename}${location.pathname}`,
        },
      ];
    }

    if (currency || date) {
      return [
        ...base,
        {
          name: formatMessage({ id: 'interbank' }),
          link: `${routerBasename}${getMbPath(currency)}`,
        },
        {
          name: pageTitle,
          link: `${routerBasename}${location.pathname}`,
        },
      ];
    }

    return [
      ...base,
      {
        name: formatMessage({ id: 'interbank' }),
        link: `${routerBasename}${location.pathname}`,
      },
    ];
  }, [
    locale,
    routerBasename,
    currency,
    date,
    location.pathname,
    formatMessage,
    isArchivePage,
    archiveBreadcrumbTitle,
    pageTitle,
  ]);

  const breadcrumbs = useBreadcrumbs(breadcrumbsPlaceholders);

  return breadcrumbs.length ? <BreadCrumbs breadcrumbs={breadcrumbs} /> : null;
};

export default memo(MbPageBreadCrumbs);
